/**
 * NOVAPAY-116 — Mini App client for sandbox payment link create/status/abandon.
 * Amount is never trusted from the client; Worker uses authoritative order total.
 * RC3 — correlation_id + timing fields; in-flight create dedupe; abandon ≠ cancel order.
 */
import { fetchWithTimeout, isFetchTimeout } from "./fetch-timeout-v2.js";
import { storeV2AuthHeaders } from "./store-v2-auth-headers-v2.js";

export type NovapayPaymentTimings = {
  session_create_ms?: number | null;
  provider_request_ms?: number | null;
  payment_url_ready_ms?: number | null;
  return_to_app_ms?: number | null;
  status_check_ms?: number | null;
  webhook_delay_ms?: number | null;
};

export type NovapayPaymentPublic = {
  payment_id: string;
  order_id: string;
  display_order_number?: string | null;
  status: string;
  customer_lifecycle?: string;
  order_payment_status?: string;
  payment_url?: string | null;
  amount_uah?: number;
  amount_minor?: number;
  currency?: string;
  expires_at?: string | null;
  failure_code?: string | null;
  paid_at?: string | null;
  correlation_id?: string | null;
  /** Raw NovaPay session status from get-status / D1 (e.g. processing). */
  provider_status?: string | null;
  timings?: NovapayPaymentTimings | null;
  customer_ui_state?: string | null;
  provider_truth_state?: string | null;
  local_provider_mismatch?: boolean;
  do_not_ask_customer_to_repay?: boolean;
  last_provider_check_at?: string | null;
};

export type NovapayCreateResult = {
  ok: boolean;
  error?: string;
  duplicate?: boolean;
  creating?: boolean;
  credentials_ready?: boolean;
  reason_codes?: string[];
  payment?: NovapayPaymentPublic;
  message?: string;
  correlation_id?: string | null;
  provider_ms?: number | null;
  timings?: NovapayPaymentTimings | null;
  order_preserved?: boolean;
};

const URL = "/api/store-v2/novapay";
/** Provider create can exceed default 8s cabinet timeout — keep status polls at 8s. */
const NOVAPAY_CREATE_TIMEOUT_MS = 15_000;
const createInflight = new Map<string, Promise<NovapayCreateResult>>();

function newClientCorrelationId(): string {
  return `npay_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export async function createNovapayPaymentV2(input: {
  orderId: string;
  sessionKey: string;
  idempotencyKey?: string | null;
  correlationId?: string | null;
}): Promise<NovapayCreateResult> {
  const key = `${input.orderId}:${input.idempotencyKey ?? "default"}`;
  const existing = createInflight.get(key);
  if (existing) return existing;

  const correlationId = String(input.correlationId ?? "").trim() || newClientCorrelationId();
  const started = performance.now();
  const promise = (async (): Promise<NovapayCreateResult> => {
    try {
      const res = await fetchWithTimeout(
        URL,
        {
          method: "POST",
          headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            action: "create",
            order_id: input.orderId,
            session_key: input.sessionKey,
            idempotency_key: input.idempotencyKey ?? null,
            correlation_id: correlationId,
          }),
        },
        NOVAPAY_CREATE_TIMEOUT_MS,
      );
      const json = (await res.json()) as NovapayCreateResult;
      const provider_ms = Math.round(performance.now() - started);
      return {
        ...json,
        correlation_id: json.correlation_id ?? json.payment?.correlation_id ?? correlationId,
        provider_ms,
        timings: {
          ...(json.timings ?? json.payment?.timings ?? {}),
          session_create_ms: json.timings?.session_create_ms ?? provider_ms,
        },
      };
    } catch (err) {
      const timeout = isFetchTimeout(err);
      return {
        ok: false,
        error: timeout ? "provider_timeout" : "network_error",
        message: timeout
          ? "Payment provider did not respond in time"
          : "Payment network error",
        correlation_id: correlationId,
        provider_ms: Math.round(performance.now() - started),
        timings: { session_create_ms: Math.round(performance.now() - started) },
      };
    } finally {
      createInflight.delete(key);
    }
  })();

  createInflight.set(key, promise);
  return promise;
}

export async function getNovapayPaymentStatusV2(input: {
  orderId: string;
  sessionKey: string;
  paymentId?: string | null;
}): Promise<NovapayCreateResult> {
  const started = performance.now();
  try {
    const qs = new URLSearchParams({
      order_id: input.orderId,
      session_key: input.sessionKey,
    });
    if (input.paymentId) qs.set("payment_id", input.paymentId);
    const res = await fetchWithTimeout(`${URL}?${qs}`, {
      method: "GET",
      headers: storeV2AuthHeaders(),
    });
    const json = (await res.json()) as NovapayCreateResult;
    const status_check_ms = Math.round(performance.now() - started);
    return {
      ...json,
      timings: { ...(json.timings ?? json.payment?.timings ?? {}), status_check_ms },
    };
  } catch {
    return {
      ok: false,
      error: "network_error",
      timings: { status_check_ms: Math.round(performance.now() - started) },
    };
  }
}

/** Abandon current payment attempt — order stays open. */
export async function abandonNovapayPaymentV2(input: {
  orderId: string;
  sessionKey: string;
  paymentId?: string | null;
}): Promise<NovapayCreateResult> {
  try {
    const res = await fetchWithTimeout(URL, {
      method: "POST",
      headers: storeV2AuthHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({
        action: "abandon",
        order_id: input.orderId,
        session_key: input.sessionKey,
        payment_id: input.paymentId ?? null,
      }),
    });
    return (await res.json()) as NovapayCreateResult;
  } catch {
    return { ok: false, error: "network_error" };
  }
}

export function novapayStatusCopy(
  lang: "ua" | "uk" | "en",
  status: string | null | undefined,
): string {
  const s = String(status ?? "");
  if (lang === "en") {
    switch (s) {
      case "creating":
      case "created":
        return "Preparing secure payment page…";
      case "link_ready":
        return "Order created. Continue to secure payment.";
      case "pending":
        return "Waiting for payment confirmation.";
      case "paid":
        return "✅ Payment received";
      case "failed":
        return "Payment was not completed. Try again or choose another method.";
      case "expired":
        return "Payment link expired. Create a new link.";
      case "abandoned":
      case "cancelled":
        return "Payment attempt cancelled. Order is saved.";
      default:
        return "Preparing secure payment…";
    }
  }
  switch (s) {
    case "creating":
    case "created":
      return "Готуємо захищену сторінку оплати…";
    case "link_ready":
      return "Замовлення створено. Перейдіть до безпечної оплати.";
    case "pending":
      return "Очікуємо підтвердження оплати.";
    case "paid":
      return "✅ Оплату отримано";
    case "failed":
      return "Оплату не завершено. Спробуйте ще раз або оберіть інший спосіб.";
    case "expired":
      return "Термін платіжного посилання минув. Створіть нове посилання.";
    case "abandoned":
    case "cancelled":
      return "Спробу оплати скасовано. Замовлення збережено.";
    default:
      return "Готуємо захищену сторінку оплати…";
  }
}
